import { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { lawyerService } from "../../services/api";
import { ROLES } from "../../config/roles";

const reqId = (r) => r?.id ?? r?.request_id;
const isPendingReq = (r) => (r?.status || "pending") === "pending";

// D-010: incoming contact requests for a lawyer, to accept/decline from the
// inbox. Only active for users with the Lawyer role; otherwise inert (empty).
// Defensive: if the backend endpoints aren't wired yet, the list stays empty.
const useLawRequests = () => {
  const roles = useSelector((state) => state.auth.roles);
  const isLawyer = Array.isArray(roles) && roles.includes(ROLES.Lawyer);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(isLawyer);

  const fetchRequests = useCallback(
    async (signal) => {
      if (!isLawyer) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const res = await lawyerService.getIncomingRequests(signal);
      if (res.success) setRequests(res.data.filter(isPendingReq));
      setLoading(false);
    },
    [isLawyer]
  );

  useEffect(() => {
    if (!isLawyer) {
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    fetchRequests(controller.signal).catch(() => setLoading(false));
    return () => controller.abort();
  }, [isLawyer, fetchRequests]);

  const removeById = (id) =>
    setRequests((prev) => prev.filter((r) => reqId(r) !== id));

  const accept = useCallback(async (id) => {
    const res = await lawyerService.acceptRequest(id);
    if (res.success) removeById(id);
    return res;
  }, []);

  const decline = useCallback(async (id) => {
    const res = await lawyerService.declineRequest(id);
    if (res.success) removeById(id);
    return res;
  }, []);

  return {
    isLawyer,
    requests,
    count: requests.length,
    loading,
    accept,
    decline,
  };
};

export default useLawRequests;
